import { useCallback } from 'react';

import { getOrRefreshToken } from '@myphx-engineering/auth/client';
import type { RichTextProps } from '@myphx-engineering/phx-design-system';
import type { PriceSubOption } from '@phx/data-sources';

import type {
    OnHoldConfig,
    ProductDisclosures,
    ProgramContentData,
} from '../configuration/shared';
import type { FillPathway } from '../contexts/fill';
import {
    type DrugProgram,
    type NdcSwitchConfig,
    type NdcSwitchConfigV2,
} from '../types/drug-config';
import type { DrugKeyType } from '../util/config/constants';

const configApiBaseUrl =
    import.meta.env.VITE_ENV === 'development'
        ? '/portal-service/config/'
        : import.meta.env.VITE_CONFIG_API_BASE_URL;
const authClientId = import.meta.env.VITE_AUTH_CLIENT_ID;
const authTokenUrl = import.meta.env.VITE_AUTH_TOKEN_URL;
const configOverrideUrl = import.meta.env.VITE_CONFIG_OVERRIDE_URL;
const contentOverrideUrl = import.meta.env.VITE_CONTENT_OVERRIDE_URL;

export class ConfigApiError extends Error {
    readonly status: number;
    readonly statusText: string;

    constructor(status: number, statusText: string, responseBody?: string) {
        super(
            `Config API request failed: ${status} ${statusText}` +
                (responseBody ? `\nResponse: ${responseBody}` : '')
        );
        this.name = this.constructor.name;
        this.status = status;
        this.statusText = statusText;
    }
}

export const isConfigApiAuthError = (error: unknown): boolean =>
    error instanceof ConfigApiError && error.status === 401;

const callConfigApi = async <T>(
    endpoint: string,
    options: RequestInit = {}
): Promise<T> => {
    const accessToken = await getOrRefreshToken(authClientId, authTokenUrl);

    let headers: Record<string, string> = {
        'Content-Type': 'application/json',
    };

    if (options.headers) {
        if (options.headers instanceof Headers) {
            options.headers.forEach((value, key) => {
                headers[key] = value;
            });
        } else if (
            typeof options.headers === 'object' &&
            !Array.isArray(options.headers)
        ) {
            headers = { ...headers, ...options.headers };
        }
    }
    if (accessToken) {
        headers['Authorization'] = `Bearer ${accessToken}`;
    }
    const response = await fetch(`${configApiBaseUrl}${endpoint}`, {
        ...options,
        headers,
    });
    if (!response.ok) {
        const errorText = await response.text();
        throw new ConfigApiError(
            response.status,
            response.statusText,
            errorText
        );
    }
    return response.json();
};

/**
 * Hook for making requests to the config API with authentication.
 */
export function useConfigApi() {
    const getProgramByAppId = useCallback(
        (appId: string) =>
            callConfigApi<DrugProgram>(`programs/by-client-id/${appId}`),
        []
    );

    return {
        getProgramByAppId,
        getConfigOverride: async (): Promise<MedicationConfigOverride> => {
            const response = await fetch(configOverrideUrl);
            if (!response.ok) {
                const errorText = await response.text();
                throw new Error(
                    `Config API request failed: ${response.status} ${response.statusText}` +
                        (errorText ? `\nResponse: ${errorText}` : '')
                );
            }
            return response.json();
        },
        getContentOverride: async (): Promise<ProgramContentData> => {
            const response = await fetch(contentOverrideUrl);
            if (!response.ok) {
                const errorText = await response.text();
                throw new Error(
                    `Config API request failed: ${response.status} ${response.statusText}` +
                        (errorText ? `\nResponse: ${errorText}` : '')
                );
            }
            return response.json();
        },
    };
}

export type HeadlinePrice = {
    type: NonNullable<PriceSubOption['priceType']>;
    value: number;
    qualifier?: {
        description?: RichTextProps & { key: string };
        text: string;
    };
    pharmacyNpis?: string[];
    content?: {
        pricingCard?: Array<RichTextProps & { key: string }>;
        priceNote?: Array<RichTextProps & { key: string }>;
    };
};

export const pathwayScreens = [
    'attestation',
    'coverage-select',
    'coverage-sub-select',
    'on-hold',
    'path-select',
    'pharmacy-preview',
    'pharmacy-select',
    'switch',
] as const;

export type PathwayScreen = (typeof pathwayScreens)[number];

export type PathwayOverride = {
    initialPathway?: FillPathway;
    initialScreen?: Extract<
        PathwayScreen,
        'attestation' | 'path-select' | 'pharmacy-select'
    >;
    favoredPathway?: FillPathway;
};

export type MedicationOverride = {
    id: string;
    ndc: string;
    description: string;
    dosageForm: 'SOAJ' | 'SOCT' | 'SOLN' | 'SOPN' | 'SOSY' | 'SUPN' | 'SUSP';
    form: string;
    packageQuantity: number;
    packageSizeUnit: string;
    strengthUnit?: string;
    strengthValue?: number;
    imageUrl?: string;
    imageOrientation?: 'portrait' | 'landscape';
    headlinePrices: HeadlinePrice[];
    pathway?: PathwayOverride;
    metadata?: {
        coverageCheckUrl?: string;
    };
};
export type PharmacyOverride = {
    name: string;
    logoUrl: string;
    logoWidth: number;
    npi: string;
    nextStepsDescription?: string;
    phone: string;
    url: string;
    contactTime: string;
    shippingTime: string;
    deliveryDays?: string;
};

export type MedicationConfigOverride = {
    constants: Record<string, string>;
    ndcInfo: Record<string, MedicationOverride>;
    drugNames: Partial<Record<DrugKeyType, string>>;
    staticPricing: {
        insurance?: Record<string, number>;
        selfPay?: Record<string, number>;
        regularPrices?: Record<string, number>;
        nonCoveredBenefit?: Record<string, number>;
    };
    pharmacyOverrides: Record<string, PharmacyOverride>;
    /**
     * @deprecated: use `ndcSwitchConfigsV2` instead
     */
    ndcSwitchConfigs?: Record<string, NdcSwitchConfig>;
    ndcSwitchConfigsV2?: Record<string, NdcSwitchConfigV2>;
    ndcHoldConfigs?: Record<string, OnHoldConfig>;
    ndcDisclosureConfigs?: Record<string, ProductDisclosures>;
};
